import { Checkbox, Flex, SoundUIProvider, Typography } from '@aloft/toolkit-sui';

import React, { ChangeEvent } from 'react';
import { Root, createRoot } from 'react-dom/client';

import { toolkitTheme } from '../../styles/toolkitTheme.ts';
import { MAP_PIN_STATUS_FILTERS, PinStatusFilter } from './constants.ts';

type StatusCheckboxesProps = {
  selected: PinStatusFilter[];
  onChange: (statuses: PinStatusFilter[]) => void;
  variant?: 'sales' | 'rental';
};

const FILTERS_SALES: PinStatusFilter[] = [
  MAP_PIN_STATUS_FILTERS['SELECTED COMPS'],
  MAP_PIN_STATUS_FILTERS.ACTIVE,
  MAP_PIN_STATUS_FILTERS.SOLD,
  MAP_PIN_STATUS_FILTERS.PENDING,
];

const statusFilterLabel = (status: PinStatusFilter, variant: 'sales' | 'rental'): string => {
  if (variant === 'rental') {
    if (status === MAP_PIN_STATUS_FILTERS.SOLD) {
      return 'Rented';
    }
    if (status === MAP_PIN_STATUS_FILTERS.ACTIVE) {
      return 'Active';
    }
    if (status === MAP_PIN_STATUS_FILTERS.PENDING) {
      return 'Pending';
    }
    if (status === MAP_PIN_STATUS_FILTERS['SELECTED COMPS']) {
      return 'Selected comps';
    }
  }
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const STATUS_FILTER_SHADOW = '0px 0px 2px 0px rgba(0,0,0,0.04), 0px 8px 8px 0px rgba(0,0,0,0.08)';

const StatusCheckboxes: React.FC<StatusCheckboxesProps> = ({
  selected,
  onChange,
  variant = 'sales',
}) => {
  const filters =
    variant === 'rental' ? [...FILTERS_SALES, MAP_PIN_STATUS_FILTERS.OTHER] : FILTERS_SALES;

  return (
    <Flex
      flexDirection="column"
      gap="4px"
      padding="8px"
      backgroundColor="surface1"
      borderRadius="8px"
      boxShadow={STATUS_FILTER_SHADOW}
    >
      {filters.map((status) => {
        const isChecked = selected.includes(status);
        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.checked ? [...selected, status] : selected.filter((s) => s !== status));
        };

        return (
          <Checkbox key={status} isChecked={isChecked} onChange={handleChange}>
            <Typography variant={isChecked ? 'h4' : 'body1'}>
              {statusFilterLabel(status, variant)}
            </Typography>
          </Checkbox>
        );
      })}
    </Flex>
  );
};

type StatusFilterControlProps = StatusCheckboxesProps;

export class StatusFilterControl {
  private container: HTMLDivElement;
  private reactRoot?: Root;
  private props: StatusFilterControlProps;

  constructor(props: StatusFilterControlProps) {
    this.props = props;
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl';
    this.container.style.margin = '0 0 12px 12px';
  }

  onAdd(): HTMLDivElement {
    this.reactRoot = createRoot(this.container);
    this.render();
    return this.container;
  }

  onRemove(): void {
    this.reactRoot?.unmount();
    this.container.remove();
  }

  updateProps(newProps: StatusFilterControlProps) {
    this.props = newProps;
    this.render();
  }

  private render() {
    this.reactRoot?.render(
      <SoundUIProvider themes={[toolkitTheme]}>
        <StatusCheckboxes {...this.props} />
      </SoundUIProvider>,
    );
  }
}
